/**
 * First-party presets seeded into bank 1 on first run. They show off layering, effects and
 * modulation, and carry energy tags for mood-matched Auto show.
 */
import type { Preset } from '../core/presets';
import type { Modulation } from '../shared/modulation';
import { newScene, type Layer, type Scene } from '../shared/scene';

const layer = (id: string, vizId: string, extra: Partial<Layer> = {}): Layer => ({
  id,
  vizId,
  params: {},
  blend: 'normal',
  opacity: 1,
  enabled: true,
  ...extra,
});

const mod = (
  id: string,
  target: string,
  source: string,
  amount: number,
  extra: Partial<Modulation> = {},
): Modulation => ({
  id,
  target,
  source,
  amount,
  curve: 'linear',
  attackMs: 20,
  releaseMs: 250,
  ...extra,
});

function scene(name: string, tags: string[], s: Partial<Scene>): Scene {
  return { ...newScene(), name, tags, ...s };
}

export const BUILT_IN_SCENES: Scene[] = [
  scene('Neon Pulse', ['energy:mid', 'style:classic'], {
    layers: [layer('l1', 'radial-pulse', { params: { symmetry: 3 } })],
    palette: { id: 'neon-night' },
    postfx: { bloom: { enabled: 1, strength: 0.7 } },
    modulations: [mod('m1', 'layer/l1/spin', 'beat.sine', 0.15)],
  }),
  scene('Laser Show', ['energy:high', 'style:lasers'], {
    layers: [
      layer('l1', 'laser-haze', { params: { beams: 11, sweep: 0.8 } }),
      layer('l2', 'edge-lasers', { blend: 'add', opacity: 0.7 }),
    ],
    palette: { id: 'spectrum' },
    postfx: { bloom: { enabled: 1, strength: 0.9, threshold: 0.4 } },
    modulations: [
      mod('m1', 'layer/l1/spread', 'audio.bass', 0.25),
      mod('m2', 'palette/shift', 'beat.bar', 0.25, { attackMs: 0, releaseMs: 0 }),
    ],
  }),
  scene('Cone Tunnel', ['energy:high', 'style:lasers'], {
    layers: [layer('l1', 'laser-cone', { params: { beams: 32, twist: 0.8 } })],
    palette: { id: 'vapor' },
    postfx: {
      feedback: { enabled: 1, amount: 0.8, zoom: 1.03, rotate: 0.01 },
      bloom: { enabled: 1, strength: 0.6 },
    },
    modulations: [
      mod('m1', 'layer/l1/rotation', 'beat.phrase', 0.3),
      mod('m2', 'fx/feedback/zoom', 'beat.pulse', 0.15),
    ],
  }),
  scene('Liquid Plasma', ['energy:low', 'style:ambient'], {
    layers: [layer('l1', 'plasma-flow', { params: { speed: 0.6, scale: 0.8 } })],
    palette: { id: 'aurora' },
    postfx: { color: { saturation: 1.2 }, lens: { vignette: 0.45 } },
    modulations: [mod('m1', 'layer/l1/scale', 'lfo.1', 0.2, { attackMs: 400, releaseMs: 800 })],
  }),
  scene('Bars & Trails', ['energy:mid', 'style:classic'], {
    layers: [layer('l1', 'spectrum-bars', { params: { bars: 96, glow: 1.4 } })],
    palette: { id: 'fire' },
    postfx: { feedback: { enabled: 1, amount: 0.7, zoom: 1.015, rotate: 0 }, mirror: { mode: 2 } },
  }),
  scene('Kaleido Scope', ['energy:mid', 'style:psychedelic'], {
    layers: [
      layer('l1', 'oscilloscope', { params: { amplitude: 1.4, thickness: 3 } }),
      layer('l2', 'plasma-flow', { blend: 'screen', opacity: 0.35 }),
    ],
    palette: { id: 'acid' },
    postfx: { mirror: { mode: 4, segments: 8, spin: 0.15 }, bloom: { enabled: 1, strength: 0.8 } },
  }),
  scene('Beat Tunnel', ['energy:high', 'style:tunnel'], {
    layers: [layer('l1', 'tunnel', { params: { speed: 1.4, twist: 1.2, segments: 12 } })],
    palette: { id: 'ocean' },
    postfx: { lens: { chromatic: 0.004, vignette: 0.4 } },
    modulations: [
      mod('m1', 'fx/lens/chromatic', 'beat.pulse', 0.5, { attackMs: 0, releaseMs: 200 }),
    ],
  }),
  scene('Haze & Bars', ['energy:mid', 'style:lasers'], {
    layers: [
      layer('l1', 'spectrum-bars', { params: { mirror: 1, height: 0.6 } }),
      layer('l2', 'laser-haze', { blend: 'add', opacity: 0.8 }),
    ],
    palette: { id: 'sunset' },
    postfx: { bloom: { enabled: 1, strength: 0.6 } },
  }),
  scene('Calm Waves', ['energy:low', 'style:ambient'], {
    layers: [layer('l1', 'oscilloscope', { params: { amplitude: 0.8, glow: 1.6, grid: 0 } })],
    palette: { id: 'ice' },
    postfx: { feedback: { enabled: 1, amount: 0.88, zoom: 1.005, rotate: 0.004, hue: 0.004 } },
    settings: { smoothing: 0.85, beatReact: 0.5 },
  }),
  scene('Gold Rush', ['energy:high', 'style:classic'], {
    layers: [
      layer('l1', 'radial-pulse', { params: { radius: 0.22, symmetry: 6, halo: 1.2 } }),
      layer('l2', 'tunnel', { blend: 'add', opacity: 0.4 }),
    ],
    palette: { id: 'gold' },
    postfx: { bloom: { enabled: 1, strength: 1, threshold: 0.45 }, mirror: { mode: 3 } },
    modulations: [mod('m1', 'layer/l2/opacity', 'audio.bass', 0.5)],
  }),
];

/** Concert-rig scenes (added later; seeded into an empty bank named "Concert"). */
export const CONCERT_SCENES: Scene[] = [
  scene('Laser Lattice', ['energy:high', 'style:lasers'], {
    layers: [
      layer('l1', 'laser-show', { params: { projectors: 5, beams: 20, pattern: 1 } }),
      layer('l2', 'crowd', { blend: 'multiply' }),
    ],
    palette: { id: 'neon-night' },
    postfx: { bloom: { enabled: 1, strength: 0.8, threshold: 0.5 } },
    modulations: [mod('m1', 'layer/l1/spread', 'lfo.2', 0.2)],
  }),
  scene('Laser Tunnel', ['energy:high', 'style:lasers'], {
    layers: [
      layer('l1', 'laser-show', { params: { projectors: 7, beams: 14, pattern: 2, colour: 1 } }),
      layer('l2', 'crowd', { blend: 'multiply', params: { hands: 0.6 } }),
    ],
    palette: { id: 'spectrum' },
    postfx: { bloom: { enabled: 1, strength: 0.9, threshold: 0.45 } },
  }),
  scene('Liquid Sky', ['energy:mid', 'style:lasers'], {
    layers: [
      layer('l1', 'liquid-sky', { params: { colour: 1 } }),
      layer('l2', 'crowd', { blend: 'multiply', params: { height: 0.18 } }),
    ],
    palette: { id: 'ocean' },
    postfx: { bloom: { enabled: 1, strength: 0.6, threshold: 0.55 } },
    modulations: [mod('m1', 'layer/l1/flow', 'mood.energy', 0.3, { releaseMs: 2000 })],
  }),
];

export function builtInPresets(): Preset[] {
  const base = BUILT_IN_SCENES.map((s, i) => ({
    id: `builtin-${i + 1}`,
    name: s.name,
    scene: s,
    builtIn: true as const,
    created: 0,
    updated: 0,
  }));
  const concert = CONCERT_SCENES.map((s, i) => ({
    id: `builtin-concert-${i + 1}`,
    name: s.name,
    scene: s,
    builtIn: true as const,
    bankName: 'Concert',
    created: 0,
    updated: 0,
  }));
  return [...base, ...concert];
}
